/**
 * The world clock: tick constants and the game calendar.
 *
 * Two rules, both from design spec §9.2 and both non-negotiable:
 *
 * - **Everything is derived from the sim-tick count.** No wall clocks, no
 *   `Date.now()`, no timers. A slow tick fires because tick 36,000 arrived,
 *   not because two and a half minutes elapsed. This is what lets a replay
 *   fast-forward 10k ticks and land in exactly the state a live server would.
 * - **Every number here is a tunable default.** The time scale in particular
 *   is a playtest question; nothing outside this file should hardcode 100 ms
 *   or 24 hours.
 *
 * The scale: **1 real hour = 1 game day.**
 */
/** Network-visible simulation rate (design spec §9.2). */
export const TICKS_PER_SECOND = 10;
/** Milliseconds of real time advanced by one sim tick. */
export const MS_PER_TICK = 1000 / TICKS_PER_SECOND;
/** Game hours in a game day. */
export const HOURS_PER_GAME_DAY = 24;
/** Game minutes in a game hour. */
export const MINUTES_PER_GAME_HOUR = 60;
/** One real hour of ticks — the game day (36,000). */
export const TICKS_PER_GAME_DAY = TICKS_PER_SECOND * 60 * 60;
/** One game hour: 2.5 real minutes (1,500 ticks). */
export const TICKS_PER_GAME_HOUR = TICKS_PER_GAME_DAY / HOURS_PER_GAME_DAY;
/** One game minute: 2.5 real seconds (25 ticks). */
export const TICKS_PER_GAME_MINUTE = TICKS_PER_GAME_HOUR / MINUTES_PER_GAME_HOUR;
/**
 * The Celtic festival year, quartered (design spec §2.4). Each season opens on
 * a festival: Calan Gaeaf, Gŵyl Fair y Canhwyllau, Calan Mai, Calan Awst.
 * Seasonal *mechanics* are post-MVP — this is the stamp narration reads from,
 * and it costs nothing to get right now rather than retrofit later.
 */
export const SEASONS = ['Gaeaf', 'Gwanwyn', 'Haf', 'Cynhaeaf'];
/** Days in each season. 4 × 91 = a 364-day year; tidy beats astronomical. */
export const DAYS_PER_SEASON = 91;
/** Days in a game year. */
export const DAYS_PER_YEAR = SEASONS.length * DAYS_PER_SEASON;
/** Resolve a tick count into a calendar stamp. */
export function gameTime(tick) {
    if (!Number.isInteger(tick) || tick < 0) {
        throw new RangeError(`gameTime needs a non-negative integer tick (got ${tick})`);
    }
    const dayIndex = Math.floor(tick / TICKS_PER_GAME_DAY);
    const dayOfYearIndex = dayIndex % DAYS_PER_YEAR;
    const seasonIndex = Math.floor(dayOfYearIndex / DAYS_PER_SEASON);
    const tickOfDay = tick % TICKS_PER_GAME_DAY;
    return {
        tick,
        day: dayIndex + 1,
        year: Math.floor(dayIndex / DAYS_PER_YEAR) + 1,
        dayOfYear: dayOfYearIndex + 1,
        season: SEASONS[seasonIndex],
        dayOfSeason: (dayOfYearIndex % DAYS_PER_SEASON) + 1,
        hour: Math.floor(tickOfDay / TICKS_PER_GAME_HOUR),
        minute: Math.floor((tickOfDay % TICKS_PER_GAME_HOUR) / TICKS_PER_GAME_MINUTE),
    };
}
const ORDINAL_ONES = [
    '',
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
    'thirteenth',
    'fourteenth',
    'fifteenth',
    'sixteenth',
    'seventeenth',
    'eighteenth',
    'nineteenth',
];
const ORDINAL_TENS = [
    '',
    '',
    'twentieth',
    'thirtieth',
    'fortieth',
    'fiftieth',
    'sixtieth',
    'seventieth',
    'eightieth',
    'ninetieth',
];
const CARDINAL_TENS = [
    '',
    '',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
];
/**
 * `1 → "first"`, `23 → "twenty-third"`. Covers 1..99, which is all a 91-day
 * season can ask for; beyond that it falls back to digits.
 */
export function ordinal(n) {
    if (!Number.isInteger(n) || n < 1 || n > 99) {
        const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
        return `${n}${suffix}`;
    }
    if (n < 20)
        return ORDINAL_ONES[n];
    const tens = Math.floor(n / 10);
    const ones = n % 10;
    if (ones === 0)
        return ORDINAL_TENS[tens];
    return `${CARDINAL_TENS[tens]}-${ORDINAL_ONES[ones]}`;
}
/** `"the third day of Gaeaf"` — the narration stamp. */
export function formatGameDate(time) {
    return `the ${ordinal(time.dayOfSeason)} day of ${time.season}`;
}
/**
 * `"07:35"` — 24-hour, zero-padded.
 *
 * Takes the two fields it reads rather than a whole `GameTime`, so the client
 * can format the hour and minute the snapshot carries (M3-05) without having to
 * reconstruct a calendar stamp it has no other use for.
 */
export function formatGameClock(time) {
    return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
}
//# sourceMappingURL=time.js.map